import React, { useState, useEffect, useRef } from "react";
import { Icon } from "semantic-ui-react";
import { toast } from "react-toastify";
import { QRCodeSVG } from "qrcode.react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { WorkPermit } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { useLanguage } from "../../../../contexts";
import { ConfirmDialog } from "../../../Shared";
import "./WorkPermitDetail.scss";

const wpController = new WorkPermit();

// ── Constantes ────────────────────────────────────────────────────────────────
const TIPO_META = {
  altura:            { tkey: "wpd_tipo_altura",            color: "#f59e0b" },
  caliente:          { tkey: "wpd_tipo_caliente",          color: "#ef4444" },
  espacio_confinado: { tkey: "wpd_tipo_espacio_confinado", color: "#14b8a6" },
  electrico_loto:    { tkey: "wpd_tipo_electrico_loto",    color: "#8b5cf6" },
  izaje:             { tkey: "wpd_tipo_izaje",             color: "#64748b" },
  quimicos:          { tkey: "wpd_tipo_quimicos",          color: "#16a34a" },
  excavacion:        { tkey: "wpd_tipo_excavacion",        color: "#d97706" },
  caldera:           { tkey: "wpd_tipo_caldera",           color: "#3b82f6" },
  demolicion:        { tkey: "wpd_tipo_demolicion",        color: "#ec4899" },
  acceso_techo:      { tkey: "wpd_tipo_acceso_techo",      color: "#06b6d4" },
  radiaciones:       { tkey: "wpd_tipo_radiaciones",       color: "#7c3aed" },
};

const STATUS_META = {
  draft:        { tkey: "wpd_status_draft",        color: "#64748b", bg: "#f1f5f9" },
  requested:    { tkey: "wpd_status_requested",    color: "#d97706", bg: "#fef9c3" },
  pending_n2:   { tkey: "wpd_status_pending_n2",   color: "#d97706", bg: "#fef3c7" },
  pending_n3:   { tkey: "wpd_status_pending_n3",   color: "#d97706", bg: "#fef3c7" },
  pending_n4:   { tkey: "wpd_status_pending_n4",   color: "#d97706", bg: "#fef3c7" },
  approved:     { tkey: "wpd_status_approved",     color: "#16a34a", bg: "#dcfce7" },
  in_execution: { tkey: "wpd_status_in_execution", color: "#16a34a", bg: "#dcfce7", dot: true },
  verified:     { tkey: "wpd_status_verified",     color: "#16a34a", bg: "#dcfce7" },
  suspended:    { tkey: "wpd_status_suspended",    color: "#ea580c", bg: "#ffedd5" },
  paused:       { tkey: "wpd_status_paused",       color: "#f97316", bg: "#fff7ed" },
  rejected:     { tkey: "wpd_status_rejected",     color: "#dc2626", bg: "#fee2e2" },
  expired:      { tkey: "wpd_status_expired",      color: "#dc2626", bg: "#fee2e2" },
  closed:       { tkey: "wpd_status_closed",       color: "#475569", bg: "#e2e8f0" },
};

const PAIS_LABELS = {
  AR: "Argentina", BO: "Bolivia",  BR: "Brasil",   CL: "Chile",
  CO: "Colombia",  CR: "Costa Rica", DO: "Rep. Dominicana", EC: "Ecuador",
  GT: "Guatemala", MX: "México",   NI: "Nicaragua", PA: "Panamá",
  PE: "Perú",      PY: "Paraguay", SV: "El Salvador", UY: "Uruguay", VE: "Venezuela",
};

const ACCION_COLOR = {
  aprobar:             { bg: "#dcfce7", color: "#16a34a" },
  aprobar_condiciones: { bg: "#fef9c3", color: "#d97706" },
  rechazar:            { bg: "#fee2e2", color: "#dc2626" },
  suspender:           { bg: "#ffedd5", color: "#ea580c" },
  solicitar_cambios:   { bg: "#eff6ff", color: "#3b82f6" },
};

const HOT_WATCH_ESTADO_OPTIONS = [
  { value: "Focos detectados — controlados",    tkey: "wpd_hw_controlled" },
  { value: "Focos detectados — no controlados", tkey: "wpd_hw_uncontrolled" },
];

// Índice de nivel activo por status
const STATUS_NIVEL_MAP = {
  requested:  0,
  pending_n2: 1,
  pending_n3: 2,
  pending_n4: 3,
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmtPermitNum = (p) => {
  if (!p.customId) return `PT-${new Date().getFullYear()}-?????`;
  const year = p.createdAt ? new Date(p.createdAt).getFullYear() : new Date().getFullYear();
  return `PT-${year}-${String(p.customId).padStart(5, "0")}`;
};

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric" }) : null;
const fmtTime = (d) =>
  d ? new Date(d).toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" }) : null;
const fmtDateTime = (d) => (d ? `${fmtDate(d)} ${fmtTime(d)}` : "—");
const fmtShort = (d) => {
  if (!d) return "—";
  return `${String(new Date(d).getDate()).padStart(2,"0")}/${String(new Date(d).getMonth()+1).padStart(2,"0")} ${fmtTime(d)}`;
};

// ── Componente principal ──────────────────────────────────────────────────────
export function WorkPermitDetail({ permit: initialPermit, onEdit, onClose, onReload, autoOpenLmra = false }) {
  const { accessToken, user } = useAuth();
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;

  const FLUJO_STR_MAP = {
    "Nivel 1 — Solicitante":           "wp_flujo_n1",
    "Nivel 2 — Supervisor del área":   "wp_flujo_n2",
    "Nivel 3 — Responsable EHS/SST":   "wp_flujo_n3",
    "Nivel 4 — Verificación in-situ":  "wp_flujo_n4",
    "Nivel 5 — Cierre":                "wp_flujo_n5",
    "Solicitante / Ejecutor":          "wp_flujo_ac_n1",
    "Supervisor del área operativa":   "wp_flujo_ac_n2",
    "Responsable EHS / SHE Coordinator": "wp_flujo_ac_n3",
    "Verificación in-situ pre-inicio": "wp_flujo_ac_n4",
    "Cierre del permiso":              "wp_flujo_ac_n5",
    "Creador del permiso":             "wp_flujo_rol_creador",
    "Supervisor del sector":           "wp_flujo_rol_supervisor_sec",
    "Aprobador de seguridad":          "wp_flujo_rol_aprobador",
    "Verificador en campo":            "wp_flujo_rol_verificador",
    "Responsable del cierre formal":   "wp_flujo_rol_cierre",
  };
  const tf = (str) => str ? t(FLUJO_STR_MAP[str] || str) : "";

  const AUDIT_EVENT_MAP = {
    "Permiso creado":                              "wp_audit_created",
    "Permiso actualizado":                         "wp_audit_updated",
    "Permiso eliminado (soft delete)":             "wp_audit_deleted",
    "Permiso duplicado (copia de borrador)":       "wp_audit_duplicated",
    "Permiso enviado a aprobación":                "wp_audit_requested",
    "Aprobado Nivel 1 — pendiente Nivel 2":        "wp_audit_pending_n2",
    "Aprobado Nivel 2 — pendiente Nivel 3":        "wp_audit_pending_n3",
    "Aprobado Nivel 3 — pendiente Nivel 4":        "wp_audit_pending_n4",
    "Todos los niveles aprobados — listo para inicio": "wp_audit_approved",
    "Trabajo iniciado":                            "wp_audit_in_execution",
    "Verificación in-situ confirmada":             "wp_audit_verified",
    "Trabajo suspendido (Stop Work Authority)":    "wp_audit_suspended",
    "Trabajo pausado":                             "wp_audit_paused",
    "Permiso rechazado":                           "wp_audit_rejected",
    "Permiso vencido":                             "wp_audit_expired",
    "Permiso vencido — expiración automática":     "wp_audit_expired_auto",
    "Permiso cerrado":                             "wp_audit_closed",
    "Permiso vuelto a borrador":                   "wp_audit_draft",
  };
  const tAudit = (evento) => {
    if (!evento) return "";
    // Eventos con comentario adjunto: "Permiso rechazado — comentario"
    for (const [key, tkey] of Object.entries(AUDIT_EVENT_MAP)) {
      if (evento === key) return t(tkey);
      if (evento.startsWith(key + " — ")) return `${t(tkey)} — ${evento.slice(key.length + 3)}`;
    }
    return evento;
  };

  const ACCION_LABELS = {
    aprobar:             t("wpd_accion_aprobado"),
    aprobar_condiciones: t("wpd_accion_aprobado_cond"),
    rechazar:            t("wpd_accion_rechazado"),
    suspender:           t("wpd_accion_suspendido"),
    solicitar_cambios:   t("wpd_accion_cambios"),
  };

  const TABS = [t("wpd_tab_permit"), t("wpd_tab_approval"), t("wpd_tab_closure"), t("wpd_tab_audit")];

  const currentUserName = [user?.firstname, user?.lastname].filter(Boolean).join(" ") || user?.email || "—";
  const [activeTab, setActiveTab] = useState(0);
  const [saving,    setSaving]    = useState(false);
  const [permit,    setPermit]    = useState(initialPermit);

  useEffect(() => { setPermit(initialPermit); }, [initialPermit]);

  // Auto-abrir modal LMRA si viene del QR y el LMRA no está completo
  useEffect(() => {
    if (!autoOpenLmra) return;
    if (initialPermit?.status !== "approved") return;
    const lmraCompleta = initialPermit?.lmra?.fecha &&
      initialPermit?.lmra?.preguntas?.every(p => p.respuesta !== null);
    if (!lmraCompleta) openLmraModal();
  }, [autoOpenLmra, initialPermit]);

  // Estado modal de aprobación
  const [approvalModal, setApprovalModal] = useState(null); // { nivelIndex, nivel, accion }
  const [approvalComment, setApprovalComment] = useState("");

  // Estado modal de cambio operativo (detener, iniciar, pausar, etc.)
  const [opModal, setOpModal] = useState(null); // { status, label, requiresComment }
  const [opComment, setOpComment] = useState("");
  const [opAccion,  setOpAccion]  = useState({ crear: false, responsable: "", dueDate: "" });

  // Acción opcional desde el cierre
  const [cierreAccion, setCierreAccion] = useState({ crear: false, responsable: "", dueDate: "" });

  // Estado modal de confirmación genérica
  const [confirmAction, setConfirmAction] = useState(null); // { message, danger, run }

  // Estado modal LMRA
  const [lmraModal,   setLmraModal]   = useState(false);
  const [lmraForm,    setLmraForm]    = useState({ fecha: "", preguntas: [...Array(4)].map(() => ({ respuesta: null, observacion: "" })) });
  const [savingLmra,  setSavingLmra]  = useState(false);

  const sm        = STATUS_META[permit.status] || STATUS_META.draft;
  const isActive  = ["approved", "in_execution", "verified", "paused"].includes(permit.status);
  const isClosed  = permit.status === "closed";
  const hasCaliente = permit.tipos?.includes("caliente");

  const siteName   = permit.site?.name   || "—";
  const sectorName = permit.sector?.name || "";
  const ubicacion  = permit.ubicacion    || "";
  const locationStr = [siteName, sectorName, ubicacion].filter(Boolean).join(" — ");
  const ejecutorStr = permit.ejecutora === "contratista"
    ? permit.contratista?.nombre || "Contratista"
    : permit.workExecutor || "";

  const startDate = fmtDate(permit.startDateTime);
  const startTime = fmtTime(permit.startDateTime);
  const endTime   = fmtTime(permit.estimatedEndDateTime);

  // ── Estado cierre ──────────────────────────────────────────────────────────
  const [cierre, setCierre] = useState({
    trabajoCompletado:   permit.cierre?.trabajoCompletado   ?? null,
    huboIncidente:       permit.cierre?.huboIncidente       ?? null,
    hotWorkWatch:        permit.cierre?.hotWorkWatch?.length
      ? permit.cierre.hotWorkWatch
      : [{ minutos: 15, hora: "", estado: "" }, { minutos: 30, hora: "", estado: "" }, { minutos: 60, hora: "", estado: "" }],
    leccionesAprendidas: permit.cierre?.leccionesAprendidas || "",
  });

  const [cierrePhotos, setCierrePhotos] = useState(permit.cierre?.fotosCierre || []);
  const [photoUrls, setPhotoUrls]       = useState({});
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const photoInputRef = useRef(null);

  const setCierreField  = (f, v) => setCierre(p => ({ ...p, [f]: v }));
  const setHotWatch = (idx, field, val) => setCierre(p => {
    const next = [...p.hotWorkWatch];
    next[idx] = { ...next[idx], [field]: val };
    return { ...p, hotWorkWatch: next };
  });

  const handleSaveCierre = async () => {
    setSaving(true);
    try {
      const payload = { cierre };
      if (cierreAccion.crear && cierre.leccionesAprendidas.trim()) {
        payload.cierreAccion = cierreAccion;
      }
      await wpController.updateWorkPermit(accessToken, permit._id, payload);
      toast.success(t("wpd_toast_cierre_saved"));
      setCierreAccion({ crear: false, responsable: "", dueDate: "" });
      setPermit(p => ({ ...p, cierre }));
      onReload?.();
    } catch { toast.error(t("wpd_toast_cierre_error")); }
    finally { setSaving(false); }
  };

  const handleCerrarDefinitivo = async () => {
    setSaving(true);
    try {
      await wpController.updateWorkPermit(accessToken, permit._id, { cierre, status: "closed" });
      toast.success(t("wpd_toast_closed"));
      setPermit(p => ({ ...p, cierre, status: "closed" }));
      onReload?.();
    } catch { toast.error(t("wpd_toast_close_error")); }
    finally { setSaving(false); }
  };

  const askCerrarDefinitivo = () => setConfirmAction({
    message: t("wpd_confirm_close_msg"),
    danger: true,
    run: handleCerrarDefinitivo,
  });

  // ── Fotos de cierre ────────────────────────────────────────────────────────
  const loadPhotoUrl = async (key) => {
    if (photoUrls[key]) return;
    const url = await wpController.getFile(accessToken, key);
    if (url) setPhotoUrls(prev => ({ ...prev, [key]: url }));
  };

  const handleUploadCierrePhoto = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploadingPhoto(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const result = await wpController.uploadCierrePhoto(accessToken, permit._id, fd);
        setCierrePhotos(prev => [...prev, result.file]);
        const url = URL.createObjectURL(file);
        setPhotoUrls(prev => ({ ...prev, [result.file.key]: url }));
      }
      toast.success(t("wpd_toast_photo_uploaded"));
      onReload?.();
    } catch { toast.error(t("wpd_toast_photo_error")); }
    finally {
      setUploadingPhoto(false);
      if (photoInputRef.current) photoInputRef.current.value = "";
    }
  };

  const handleDeleteCierrePhoto = async (photo) => {
    try {
      await wpController.deleteCierrePhoto(accessToken, permit._id, photo.key);
      setCierrePhotos(prev => prev.filter(p => p.key !== photo.key));
      toast.success(t("wpd_toast_photo_deleted"));
      onReload?.();
    } catch { toast.error(t("wpd_toast_photo_delete_error")); }
  };

  const askDeleteCierrePhoto = (photo) => setConfirmAction({
    message: t("wpd_confirm_delete_photo"),
    danger: true,
    run: () => handleDeleteCierrePhoto(photo),
  });

  // ── Descarga QR ───────────────────────────────────────────────────────────
  const qrRef = useRef(null);
  const handleDownloadQR = () => {
    const svg = qrRef.current?.querySelector("svg");
    if (!svg) return;
    const permitRef = fmtPermitNum(permit);
    const canvas = document.createElement("canvas");
    const size = 300;
    canvas.width  = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d");
    const xml = new XMLSerializer().serializeToString(svg);
    const img = new Image();
    img.onload = () => {
      ctx.fillStyle = "#fff";
      ctx.fillRect(0, 0, size, size);
      ctx.drawImage(img, 0, 0, size, size);
      const a = document.createElement("a");
      a.href     = canvas.toDataURL("image/png");
      a.download = `QR-${permitRef}.png`;
      a.click();
    };
    img.src = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(xml)))}`;
  };

  // ── LMRA in-situ ──────────────────────────────────────────────────────────
  const openLmraModal = () => {
    setLmraForm({
      fecha: new Date().toISOString().slice(0, 16),
      preguntas: [...Array(4)].map(() => ({ respuesta: null, observacion: "" })),
    });
    setLmraModal(true);
  };

  const setLmraPregunta = (i, field, value) =>
    setLmraForm(p => {
      const next = [...p.preguntas];
      next[i] = { ...next[i], [field]: value };
      return { ...p, preguntas: next };
    });

  const handleSaveLmra = async () => {
    if (!lmraForm.fecha) return toast.warning(t("wpd_toast_lmra_date"));
    if (lmraForm.preguntas.some(p => p.respuesta === null)) return toast.warning(t("wpd_toast_lmra_questions"));

    setSavingLmra(true);
    const tieneNo = lmraForm.preguntas.some(p => p.respuesta === false);
    try {
      await wpController.updateWorkPermit(accessToken, permit._id, {
        lmra: { fecha: new Date(lmraForm.fecha).toISOString(), preguntas: lmraForm.preguntas },
      });
      if (!tieneNo) {
        await wpController.changeStatus(accessToken, permit._id, { status: "in_execution" });
        toast.success(t("wpd_toast_lmra_ok"));
      } else {
        toast.warning(t("wpd_toast_lmra_obs"));
      }
      setLmraModal(false);
      onReload?.();
    } catch (e) {
      toast.error(e?.msg || t("wpd_toast_lmra_error"));
    } finally { setSavingLmra(false); }
  };

  // ── Duplicar permiso ───────────────────────────────────────────────────────
  const handleDuplicate = async () => {
    setSaving(true);
    try {
      const result = await wpController.duplicateWorkPermit(accessToken, permit._id);
      toast.success(t("wpd_toast_dup_prefix") + fmtPermitNum(result.workPermit));
      onReload?.();
    } catch (err) {
      toast.error(err?.msg || t("wpd_toast_dup_error"));
    } finally { setSaving(false); }
  };

  const askDuplicate = () => setConfirmAction({
    message: t("wpd_confirm_duplicate_msg"),
    run: handleDuplicate,
  });

  // ── Exportar PDF ───────────────────────────────────────────────────────────
  const handleExportPDF = () => {
    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const W = doc.internal.pageSize.getWidth();
    let y = 15;

    const addSection = (title) => {
      if (y > 250) { doc.addPage(); y = 15; }
      doc.setFontSize(8).setTextColor(100);
      doc.text(title, 14, y);
      doc.setDrawColor(200).line(14, y + 1, W - 14, y + 1);
      y += 6;
      doc.setTextColor(30);
    };

    // Encabezado
    doc.setFontSize(16).setFont("helvetica", "bold").setTextColor(30);
    doc.text(t("wpd_pdf_title"), 14, y);
    doc.setFontSize(11).setFont("helvetica", "normal");
    doc.text(fmtPermitNum(permit), W - 14, y, { align: "right" });
    y += 7;
    doc.setFontSize(9).setTextColor(100);
    doc.text(`${t("wpd_pdf_status_label")}: ${t(STATUS_META[permit.status]?.tkey) || permit.status}`, 14, y);
    doc.text(`${t("wpd_pdf_generated")}: ${fmtDateTime(new Date())}`, W - 14, y, { align: "right" });
    y += 10;

    addSection(t("wpd_pdf_general_data"));
    autoTable(doc, {
      startY: y,
      margin: { left: 14, right: 14 },
      theme: "plain",
      styles: { fontSize: 8, cellPadding: 1.5 },
      columnStyles: { 0: { fontStyle: "bold", cellWidth: 50 } },
      body: [
        [t("wpd_pdf_description"),  permit.descripcion || "—"],
        [t("wpd_pdf_site_sector"),  locationStr || "—"],
        [t("wpd_pdf_country"),      PAIS_LABELS[permit.pais] || permit.pais || "—"],
        [t("wpd_pdf_types"),        permit.tipos?.map(tipo => t(TIPO_META[tipo]?.tkey) || tipo).join(", ") || "—"],
        [t("wpd_pdf_start_date"),   fmtDateTime(permit.startDateTime)],
        [t("wpd_pdf_end_date"),     fmtDateTime(permit.estimatedEndDateTime)],
        [t("wpd_pdf_executor"),     ejecutorStr || "—"],
        [t("wpd_pdf_vigia"),        permit.vigia || "—"],
      ],
    });
    y = doc.lastAutoTable.finalY + 8;

    if (permit.personal?.length > 0) {
      addSection(t("wpd_pdf_personnel"));
      autoTable(doc, {
        startY: y,
        margin: { left: 14, right: 14 },
        theme: "striped",
        headStyles: { fillColor: [71, 85, 105], fontSize: 7 },
        styles: { fontSize: 7, cellPadding: 1.5 },
        head: [[t("wpd_pdf_name"), t("wpd_pdf_role")]],
        body: permit.personal.map(p => [
          p.nombre || "—",
          p.cargo  || "—",
        ]),
      });
      y = doc.lastAutoTable.finalY + 8;
    }

    if (permit.flujoAprobacion?.length > 0) {
      addSection(t("wpd_pdf_approval_flow"));
      autoTable(doc, {
        startY: y,
        margin: { left: 14, right: 14 },
        theme: "striped",
        headStyles: { fillColor: [71, 85, 105], fontSize: 7 },
        styles: { fontSize: 7, cellPadding: 1.5 },
        head: [[t("wpd_pdf_level"), t("wpd_pdf_rol"), t("wpd_pdf_action"), t("wpd_pdf_approved_by"), t("wpd_pdf_date"), t("wpd_pdf_comment")]],
        body: permit.flujoAprobacion.map(n => [
          tf(n.nivel) || "—",
          tf(n.rol)   || "—",
          ACCION_LABELS[n.accionRealizada] || (n.approvedAt ? t("wpd_pdf_approved") : t("wpd_pdf_pending")),
          n.approvedBy || "—",
          n.approvedAt ? fmtShort(n.approvedAt) : "—",
          n.comentario || "—",
        ]),
      });
      y = doc.lastAutoTable.finalY + 8;
    }

    if (permit.auditLog?.length > 0) {
      addSection(t("wpd_pdf_audit_log"));
      autoTable(doc, {
        startY: y,
        margin: { left: 14, right: 14 },
        theme: "plain",
        headStyles: { fillColor: [71, 85, 105], fontSize: 7 },
        styles: { fontSize: 7, cellPadding: 1.5 },
        head: [[t("wpd_pdf_event"), t("wpd_pdf_user"), t("wpd_pdf_date"), "IP"]],
        body: permit.auditLog.map(e => [
          tAudit(e.evento) || "—",
          e.userName || "—",
          e.createdAt ? fmtShort(e.createdAt) : "—",
          e.ip || "—",
        ]),
      });
    }

    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(7).setTextColor(150);
      doc.text(`${fmtPermitNum(permit)} · ${t("wpd_pdf_page")} ${i} de ${pageCount}`, W / 2, 290, { align: "center" });
    }

    doc.save(`${fmtPermitNum(permit)}.pdf`);
    toast.success(t("wpd_toast_pdf"));
  };

  // ── Aprobación de nivel ────────────────────────────────────────────────────
  const openApprovalModal = (nivelIndex, nivel, accion) => {
    setApprovalComment("");
    setApprovalModal({ nivelIndex, nivel, accion });
  };

  const handleApproveLevel = async () => {
    if (!approvalModal) return;
    const { nivelIndex, nivel, accion } = approvalModal;
    const comentObligatorio = permit.flujoAprobacion?.[nivelIndex]?.comentarioObligatorio;
    if (comentObligatorio && ["rechazar", "suspender", "solicitar_cambios"].includes(accion) && !approvalComment.trim()) {
      toast.warning(t("wpd_toast_comment_required"));
      return;
    }
    setSaving(true);
    try {
      const result = await wpController.approveLevel(accessToken, permit._id, {
        accion,
        comentario: approvalComment.trim(),
      });
      toast.success(t("wpd_toast_accion_pre") + (ACCION_LABELS[accion] || accion) + t("wpd_toast_accion_post"));
      setPermit(prev => {
        const flujo = [...(prev.flujoAprobacion || [])];
        flujo[nivelIndex] = {
          ...flujo[nivelIndex],
          approvedBy:      currentUserName,
          approvedAt:      new Date().toISOString(),
          accionRealizada: accion,
          comentario:      approvalComment.trim(),
        };
        return { ...prev, flujoAprobacion: flujo, status: result.status };
      });
      setApprovalModal(null);
      onReload?.();
    } catch (err) {
      toast.error(err?.msg || t("wpd_toast_ap_error"));
    } finally {
      setSaving(false);
    }
  };

  // ── Cambio de estado operativo ─────────────────────────────────────────────
  const openOpModal = (status, label, requiresComment = false) => {
    setOpComment("");
    setOpAccion({ crear: false, responsable: "", dueDate: "" });
    setOpModal({ status, label, requiresComment });
  };

  const handleChangeStatus = async () => {
    if (!opModal) return;
    if (opModal.requiresComment && !opComment.trim()) {
      toast.warning(t("wpd_toast_reason_required"));
      return;
    }
    setSaving(true);
    try {
      const accionPayload = ["suspended", "verified"].includes(opModal.status) && opAccion.crear
        ? { accion: opAccion }
        : {};
      const result = await wpController.changeStatus(accessToken, permit._id, {
        status:     opModal.status,
        comentario: opComment.trim(),
        ...accionPayload,
      });
      toast.success(t("wpd_toast_status_pre") + (t(STATUS_META[opModal.status]?.tkey) || opModal.status) + t("wpd_toast_status_post"));
      setPermit(prev => ({ ...prev, status: result.status }));
      setOpModal(null);
      onReload?.();
    } catch (err) {
      toast.error(err?.msg || t("wpd_toast_status_error"));
    } finally {
      setSaving(false);
    }
  };

  // Nivel pendiente activo según status actual
  const pendingNivelIndex = STATUS_NIVEL_MAP[permit.status] ?? -1;

  return (
    <div className="wpd">

      {/* ── Header ── */}
      <div className="wpd__header">
        <div className="wpd__header-main">
          <p className="wpd__header-eyebrow">{t("wpd_header_eyebrow")}</p>
          <div className="wpd__header-top">
            <h1 className="wpd__permit-num">{fmtPermitNum(permit)}</h1>
            <span className="wpd__status-badge" style={{ color: sm.color, background: sm.bg }}>
              {sm.dot && <span className="wpd__status-dot" />}
              {t(sm.tkey)}
            </span>
            {permit.pais && <span className="wpd__pais-badge">{PAIS_LABELS[permit.pais] || permit.pais}</span>}
          </div>
          {permit.descripcion && <p className="wpd__desc">{permit.descripcion}</p>}
          <div className="wpd__meta-row">
            {locationStr && <span className="wpd__meta-item"><Icon name="map marker alternate" />{locationStr}</span>}
            {ejecutorStr  && <span className="wpd__meta-item"><Icon name="user outline" />{ejecutorStr}</span>}
            {startDate    && (
              <span className="wpd__meta-item">
                <Icon name="calendar outline" />
                {startDate}{startTime ? ` · ${startTime}${endTime ? ` – ${endTime}` : ""}` : ""}
              </span>
            )}
          </div>
          {permit.tipos?.length > 0 && (
            <div className="wpd__tipos">
              {permit.tipos.map(tipo => {
                const meta = TIPO_META[tipo];
                if (!meta) return null;
                return (
                  <span key={tipo} className="wpd__tipo-badge"
                    style={{ color: meta.color, background: `${meta.color}18`, borderColor: `${meta.color}40` }}>
                    {t(meta.tkey)}
                  </span>
                );
              })}
            </div>
          )}
        </div>

        <div className="wpd__header-actions">
          {["approved", "in_execution", "paused", "verified", "suspended"].includes(permit.status) && (
            <div className="wpd__qr-box" ref={qrRef}>
              <QRCodeSVG
                value={`${window.location.origin}/admin/work-permits/${permit._id}?action=lmra`}
                size={72}
                level="M"
              />
              <div className="wpd__qr-info">
                <span className="wpd__qr-title">{t("wpd_qr_title")}</span>
                <span className="wpd__qr-sub">{t("wpd_qr_sub").split("\n").map((line, i) => <React.Fragment key={i}>{line}{i === 0 && <br />}</React.Fragment>)}</span>
                <button className="wpd__qr-download" onClick={handleDownloadQR} title={t("wpd_qr_download")}>
                  <Icon name="download" />{t("wpd_qr_download")}
                </button>
              </div>
            </div>
          )}

          {/* Botones principales */}
          <div className="wpd__action-btns">
            <button className="wpd__btn wpd__btn--outline" onClick={handleExportPDF}>
              <Icon name="download" />PDF
            </button>
            <button className="wpd__btn wpd__btn--outline" onClick={askDuplicate} disabled={saving}>
              <Icon name="copy outline" />{t("wpd_btn_duplicate")}
            </button>
            <button className="wpd__btn wpd__btn--edit" onClick={() => onEdit(permit)}>
              <Icon name="edit outline" />{t("wpd_btn_edit")}
            </button>
          </div>

          {/* Botones operativos según estado */}
          <div className="wpd__op-btns">
            {permit.status === "approved" && !permit.lmra?.fecha && (
              <button
                className="wpd__btn wpd__btn--lmra"
                onClick={openLmraModal}
                disabled={saving}
              >
                <Icon name="clipboard check" />{t("wpd_btn_complete_lmra")}
              </button>
            )}
            {permit.status === "approved" && !!permit.lmra?.fecha && (
              <button
                className="wpd__btn wpd__btn--start"
                onClick={() => {
                  const lmraOk = permit.lmra?.preguntas?.length === 4 &&
                    permit.lmra.preguntas.every(p => p.respuesta !== null);
                  if (!lmraOk) {
                    openLmraModal();
                    toast.info(t("wpd_toast_lmra_complete"));
                    return;
                  }
                  openOpModal("in_execution", t("wpd_btn_start_work"));
                }}
                disabled={saving}
              >
                <Icon name="play" />{t("wpd_btn_start_work")}
              </button>
            )}
            {permit.status === "in_execution" && (
              <button
                className="wpd__btn wpd__btn--pause"
                onClick={() => openOpModal("paused", t("wpd_btn_pause_work"))}
                disabled={saving}
              >
                <Icon name="pause" />{t("wpd_btn_pause")}
              </button>
            )}
            {permit.status === "paused" && (
              <button
                className="wpd__btn wpd__btn--start"
                onClick={() => openOpModal("in_execution", t("wpd_btn_resume_work"))}
                disabled={saving}
              >
                <Icon name="play" />{t("wpd_btn_resume")}
              </button>
            )}
            {permit.status === "suspended" && (
              <button
                className="wpd__btn wpd__btn--start"
                onClick={() => openOpModal("in_execution", t("wpd_btn_reactivate_work"), false)}
                disabled={saving}
              >
                <Icon name="redo" />{t("wpd_btn_reactivate")}
              </button>
            )}
            {isActive && (
              <button
                className="wpd__btn wpd__btn--stop"
                onClick={() => openOpModal("suspended", t("wpd_btn_stop_work_swa"), true)}
                disabled={saving}
              >
                <Icon name="stop circle" />{t("wpd_btn_stop_work")}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Banner SWA ── */}
      {isActive && (
        <div className="wpd__swa-banner">
          <Icon name="warning sign" />
          <strong>{t("wpd_swa_active")}</strong>{" "}
          {t("wpd_swa_text")}{" "}
          <strong>{t("wpd_swa_no_reprisals")}</strong>
        </div>
      )}
      {permit.status === "suspended" && (
        <div className="wpd__suspended-banner">
          <Icon name="ban" />
          <strong>{t("wpd_suspended_title")}</strong>{" "}
          {t("wpd_suspended_text")}
        </div>
      )}

      {/* ── Tabs ── */}
      <div className="wpd__tabs">
        {TABS.map((label, i) => (
          <button key={i}
            className={`wpd__tab${activeTab === i ? " wpd__tab--active" : ""}`}
            onClick={() => setActiveTab(i)}>
            {label}
            {i === 1 && pendingNivelIndex >= 0 && (
              <span className="wpd__tab-dot" />
            )}
          </button>
        ))}
      </div>

      {/* ── Cuerpo ── */}
      <div className="wpd__body">

        {/* ══ Tab 0: Permiso ══ */}
        {activeTab === 0 && (
          <div className="wpd__tab-permiso">
            <div className="wpd__tab-left">
              <div className="wpd__card">
                <div className="wpd__card-header">{t("wpd_work_desc")}</div>
                <div className="wpd__card-body">
                  <p className="wpd__desc-text">{permit.descripcion || "—"}</p>
                </div>
              </div>

              {permit.personal?.length > 0 && (
                <div className="wpd__card">
                  <div className="wpd__card-header">{t("wpd_authorized_personnel")}</div>
                  <div className="wpd__card-body">
                    <div className="wpd__personal-list">
                      {permit.personal.map((p, i) => (
                        <div key={i} className="wpd__personal-row">
                          <span className="wpd__personal-name">
                            {p.nombre}{p.cargo ? ` · ${p.cargo}` : ""}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {permit.vigia && (
                <div className="wpd__card">
                  <div className="wpd__card-header">{t("wpd_vigia")}</div>
                  <div className="wpd__card-body">
                    <p className="wpd__vigia-name">{permit.vigia}</p>
                  </div>
                </div>
              )}

              {permit.ats && (
                <div className="wpd__card">
                  <div className="wpd__card-header">{t("wpd_risk_analysis")}</div>
                  <div className="wpd__card-body">
                    <p className="wpd__ats-line">
                      <Icon name="check circle" color="green" />
                      {permit.ats.modo === "jsa"       && t("wpd_ats_jsa")}
                      {permit.ats.modo === "vinculado"  && `${t("wpd_ats_linked")} ${permit.ats.vinculoLabel || permit.ats.vinculo || "EvR"}`}
                      {permit.ats.modo === "externo"    && `${t("wpd_ats_external")} ${permit.ats.externoQuien || ""}`}
                      {!permit.ats.modo                && t("wpd_ats_none")}
                    </p>
                  </div>
                </div>
              )}

            </div>

            <div className="wpd__tab-right">
              <div className="wpd__card">
                <div className="wpd__card-header">{t("wpd_approval_flow")}</div>
                <div className="wpd__card-body">
                  <div className="wpd__flujo-timeline">
                    {permit.flujoAprobacion?.map((n, i) => {
                      const done = !!n.approvedAt;
                      return (
                        <div key={i} className={`wpd__flujo-item${done ? " wpd__flujo-item--done" : ""}`}>
                          <div className="wpd__flujo-left">
                            <div className="wpd__flujo-circle">
                              {done ? <Icon name="check" /> : null}
                            </div>
                            {i < permit.flujoAprobacion.length - 1 && <div className="wpd__flujo-line" />}
                          </div>
                          <div className="wpd__flujo-body">
                            <p className="wpd__flujo-nivel">{tf(n.nivel) || `Nivel ${i + 1}`}</p>
                            <p className="wpd__flujo-rol">{tf(n.rol)}</p>
                            {done && (
                              <p className="wpd__flujo-meta">
                                {n.approvedBy}{n.approvedAt ? ` · ${fmtShort(n.approvedAt)}` : ""}
                              </p>
                            )}
                            {done && n.comentario && (
                              <p className="wpd__flujo-comentario">« {n.comentario} »</p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ══ Tab 1: Aprobación ══ */}
        {activeTab === 1 && (
          <div className="wpd__tab-content">
            <p className="wpd__section-label">{t("wpd_approval_actions_label")}</p>

            <div className="wpd__ap-panel">
              {permit.flujoAprobacion?.map((n, i) => {
                const done    = !!n.approvedAt;
                const isPending = i === pendingNivelIndex && !done;
                const blocked = !done && !isPending;
                const accionColor = ACCION_COLOR[n.accionRealizada];

                return (
                  <div
                    key={i}
                    className={`wpd__ap-card${done ? " wpd__ap-card--done" : ""}${isPending ? " wpd__ap-card--pending" : ""}${blocked ? " wpd__ap-card--blocked" : ""}`}
                  >
                    <div className="wpd__ap-card-num">{i + 1}</div>

                    <div className="wpd__ap-card-body">
                      <div className="wpd__ap-card-header">
                        <span className="wpd__ap-card-nivel">{tf(n.nivel) || `Nivel ${i + 1}`}</span>
                        <span className="wpd__ap-card-rol">{tf(n.rol)}</span>
                        {done && accionColor && (
                          <span className="wpd__ap-badge wpd__ap-badge--ok"
                            style={{ background: accionColor.bg, color: accionColor.color }}>
                            {ACCION_LABELS[n.accionRealizada] || n.accionRealizada}
                          </span>
                        )}
                        {isPending && (
                          <span className="wpd__ap-badge wpd__ap-badge--pending">{t("wpd_ap_waiting")}</span>
                        )}
                        {blocked && (
                          <span className="wpd__ap-badge wpd__ap-badge--blocked">{t("wpd_ap_blocked")}</span>
                        )}
                      </div>

                      {done && (
                        <div className="wpd__ap-card-meta">
                          <span>{n.approvedBy}</span>
                          {n.approvedAt && <span>· {fmtDateTime(n.approvedAt)}</span>}
                          {n.comentario && <span className="wpd__ap-card-comment">« {n.comentario} »</span>}
                        </div>
                      )}

                      {isPending && (
                        <div className="wpd__ap-card-actions">
                          {n.acciones?.map(accion => {
                            const ac = ACCION_COLOR[accion];
                            return (
                              <button
                                key={accion}
                                className="wpd__ap-action-btn"
                                style={ac ? { background: ac.bg, color: ac.color, borderColor: `${ac.color}40` } : {}}
                                onClick={() => openApprovalModal(i, tf(n.nivel), accion)}
                                disabled={saving}
                              >
                                {ACCION_LABELS[accion] || accion}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}

              {pendingNivelIndex === -1 && !["draft", "rejected", "suspended", "paused"].includes(permit.status) && (
                <p className="wpd__ap-done-msg">
                  {permit.status === "approved"     && t("wpd_ap_done_approved")}
                  {permit.status === "in_execution" && t("wpd_ap_done_execution")}
                  {permit.status === "verified"     && t("wpd_ap_done_verified")}
                  {permit.status === "closed"       && t("wpd_ap_done_closed")}
                  {permit.status === "expired"      && t("wpd_ap_done_expired")}
                </p>
              )}
            </div>

            {/* Historial completo */}
            <p className="wpd__section-label" style={{ marginTop: "1.75rem" }}>{t("wpd_history_label")}</p>
            <table className="wpd__aprobacion-table">
              <thead>
                <tr>
                  <th>{t("wpd_col_level")}</th>
                  <th>{t("wpd_col_user")}</th>
                  <th>{t("wpd_col_status")}</th>
                  <th>{t("wpd_col_date")}</th>
                  <th>{t("wpd_col_comment")}</th>
                </tr>
              </thead>
              <tbody>
                {permit.flujoAprobacion?.map((n, i) => {
                  const done    = !!n.approvedAt;
                  const accion  = ACCION_LABELS[n.accionRealizada] || (done ? t("wpd_accion_aprobado") : null);
                  const blocked = !done && i !== pendingNivelIndex;
                  return (
                    <tr key={i}>
                      <td className="wpd__ap-nivel">{tf(n.nivel) || `Nivel ${i + 1}`}</td>
                      <td className="wpd__ap-usuario">{n.approvedBy || "—"}</td>
                      <td>
                        {done ? (
                          <span className="wpd__ap-badge wpd__ap-badge--ok">{accion || t("wpd_accion_aprobado")}</span>
                        ) : blocked ? (
                          <span className="wpd__ap-badge wpd__ap-badge--blocked">{t("wpd_ap_blocked")}</span>
                        ) : (
                          <span className="wpd__ap-badge wpd__ap-badge--pending">{t("wpd_ap_pending")}</span>
                        )}
                      </td>
                      <td className="wpd__ap-fecha">
                        {n.approvedAt ? fmtShort(n.approvedAt) : "—"}
                      </td>
                      <td className="wpd__ap-comentario">{n.comentario || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* ══ Tab 2: Cierre ══ */}
        {activeTab === 2 && (
          <div className="wpd__tab-content">
            <p className="wpd__cierre-title">{t("wpd_closure_title")}</p>

            <div className="wpd__cierre-row">
              <div className="wpd__cierre-field">
                <p className="wpd__cierre-label">{t("wpd_work_completed")}</p>
                <div className="wpd__yn-group">
                  <button className={`wpd__yn-btn${cierre.trabajoCompletado === true ? " wpd__yn-btn--active" : ""}`}
                    onClick={() => setCierreField("trabajoCompletado", true)} disabled={isClosed}>{t("raf_yes")}</button>
                  <button className={`wpd__yn-btn${cierre.trabajoCompletado === false ? " wpd__yn-btn--active" : ""}`}
                    onClick={() => setCierreField("trabajoCompletado", false)} disabled={isClosed}>{t("raf_no")}</button>
                </div>
              </div>
              <div className="wpd__cierre-field">
                <p className="wpd__cierre-label">{t("wpd_had_incident")}</p>
                <div className="wpd__yn-group">
                  <button className={`wpd__yn-btn${cierre.huboIncidente === true ? " wpd__yn-btn--active" : ""}`}
                    onClick={() => setCierreField("huboIncidente", true)} disabled={isClosed}>{t("raf_yes")}</button>
                  <button className={`wpd__yn-btn${cierre.huboIncidente === false ? " wpd__yn-btn--active" : ""}`}
                    onClick={() => setCierreField("huboIncidente", false)} disabled={isClosed}>{t("raf_no")}</button>
                </div>
              </div>
            </div>

            {hasCaliente && (
              <div className="wpd__hotwatch">
                <p className="wpd__hotwatch-title">
                  {t("wpd_hotwatch_title")}
                </p>
                <div className="wpd__hotwatch-grid">
                  {cierre.hotWorkWatch.map((hw, i) => (
                    <div key={i} className="wpd__hotwatch-col">
                      <p className="wpd__hotwatch-min">{hw.minutos} MIN</p>
                      <input type="time" className="wpd__hotwatch-time"
                        value={hw.hora} onChange={e => setHotWatch(i, "hora", e.target.value)} disabled={isClosed} />
                      <select className="wpd__hotwatch-sel"
                        value={hw.estado} onChange={e => setHotWatch(i, "estado", e.target.value)} disabled={isClosed}>
                        <option value="">{t("wpd_hw_no_foci")}</option>
                        {HOT_WATCH_ESTADO_OPTIONS.map(o => <option key={o.value} value={o.value}>{t(o.tkey)}</option>)}
                      </select>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <p className="wpd__cierre-label" style={{ marginTop: "1.25rem" }}>
              {t("wpd_lessons_label")}
            </p>
            <textarea className="wpd__cierre-textarea" rows={4}
              placeholder={t("wpd_lessons_ph")}
              value={cierre.leccionesAprendidas}
              onChange={e => setCierreField("leccionesAprendidas", e.target.value)}
              disabled={isClosed} />

            {!isClosed && cierre.leccionesAprendidas.trim() && (
              <div className="wpd__cierre-accion">
                <label className="wpd__cierre-accion__toggle">
                  <input
                    type="checkbox"
                    checked={cierreAccion.crear}
                    onChange={e => setCierreAccion(p => ({ ...p, crear: e.target.checked }))}
                  />
                  {t("wpd_generate_action")}
                </label>
                {cierreAccion.crear && (
                  <div className="wpd__cierre-accion__fields">
                    <div>
                      <label className="wpd__cierre-label">{t("wpd_responsible")}</label>
                      <input
                        className="wpd__cierre-input"
                        type="text"
                        placeholder={t("wpd_responsible_ph")}
                        value={cierreAccion.responsable}
                        onChange={e => setCierreAccion(p => ({ ...p, responsable: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="wpd__cierre-label">{t("wpd_due_date")}</label>
                      <input
                        className="wpd__cierre-input"
                        type="date"
                        value={cierreAccion.dueDate}
                        onChange={e => setCierreAccion(p => ({ ...p, dueDate: e.target.value }))}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            <p className="wpd__cierre-label" style={{ marginTop: "1.25rem" }}>
              {t("wpd_photo_evidence")}
            </p>

            {cierrePhotos.length > 0 && (
              <div className="wpd__cierre-photos">
                {cierrePhotos.map((photo, idx) => {
                  const url = photoUrls[photo.key];
                  if (!url) loadPhotoUrl(photo.key);
                  return (
                    <div key={photo.key || idx} className="wpd__cierre-photo-item">
                      {url ? (
                        <img
                          src={url}
                          alt={photo.name}
                          className="wpd__cierre-photo-thumb"
                          onClick={() => window.open(url, "_blank")}
                        />
                      ) : (
                        <div className="wpd__cierre-photo-thumb" style={{ display: "flex", alignItems: "center", justifyContent: "center", background: "#f1f5f9" }}>
                          <Icon name="image" size="large" color="grey" />
                        </div>
                      )}
                      {!isClosed && (
                        <button
                          className="wpd__cierre-photo-del"
                          onClick={() => askDeleteCierrePhoto(photo)}
                          title={t("wpd_confirm_delete_photo")}
                        >×</button>
                      )}
                      <span className="wpd__cierre-photo-name">{photo.name}</span>
                    </div>
                  );
                })}
              </div>
            )}

            {!isClosed && (
              <>
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/*,.pdf"
                  multiple
                  style={{ display: "none" }}
                  onChange={handleUploadCierrePhoto}
                />
                <div
                  className="wpd__upload-zone"
                  onClick={() => !uploadingPhoto && photoInputRef.current?.click()}
                  style={{ cursor: uploadingPhoto ? "default" : "pointer" }}
                >
                  <Icon name="cloud upload" size="large" />
                  <p>{uploadingPhoto ? t("wpd_uploading") : t("wpd_upload_ph")}</p>
                </div>
              </>
            )}

            {!isClosed && (
              <div className="wpd__cierre-footer">
                <button className="wpd__btn wpd__btn--outline" onClick={handleSaveCierre} disabled={saving}>
                  {t("wpd_save_progress")}
                </button>
                <button className="wpd__btn wpd__btn--close" onClick={askCerrarDefinitivo} disabled={saving}>
                  <Icon name="lock" />{t("wpd_close_definitive")}
                </button>
              </div>
            )}
          </div>
        )}

        {/* ══ Tab 3: Auditoría ══ */}
        {activeTab === 3 && (
          <div className="wpd__tab-content">
            <p className="wpd__audit-title">{t("wpd_audit_title")}</p>
            {permit.auditLog?.length > 0 ? (
              <div className="wpd__audit-log">
                {permit.auditLog.map((entry, i) => (
                  <div key={i} className="wpd__audit-entry">
                    <div className="wpd__audit-dot" />
                    <div className="wpd__audit-body">
                      <p className="wpd__audit-evento">{tAudit(entry.evento)}</p>
                      <p className="wpd__audit-meta">
                        {[
                          entry.userName,
                          entry.createdAt ? `${fmtDate(entry.createdAt)} ${fmtTime(entry.createdAt)}` : null,
                          entry.ip ? `IP ${entry.ip}` : null,
                        ].filter(Boolean).join(" · ")}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="wpd__empty-msg">{t("wpd_no_audit")}</p>
            )}
          </div>
        )}

      </div>

      {/* ── Modal de aprobación de nivel ── */}
      {approvalModal && (
        <div className="wpd__modal-overlay" onClick={() => setApprovalModal(null)}>
          <div className="wpd__modal" onClick={e => e.stopPropagation()}>
            <div className="wpd__modal-header">
              <span className="wpd__modal-title">{t("wpd_modal_confirm_action")}</span>
              <button className="wpd__modal-close" onClick={() => setApprovalModal(null)}>×</button>
            </div>
            <div className="wpd__modal-body">
              <p className="wpd__modal-desc">
                {t("wpd_modal_action_label")} <strong style={ACCION_COLOR[approvalModal.accion]}>
                  {ACCION_LABELS[approvalModal.accion] || approvalModal.accion}
                </strong>
              </p>
              <p className="wpd__modal-desc">
                {t("wpd_modal_level_label")} <strong>{approvalModal.nivel}</strong>
              </p>
              <label className="wpd__modal-label">
                {t("wpd_modal_comment_label")}
                {["rechazar", "suspender", "solicitar_cambios"].includes(approvalModal.accion) && (
                  <span className="wpd__modal-req"> *</span>
                )}
              </label>
              <textarea
                className="wpd__modal-textarea"
                rows={3}
                placeholder={t("wpd_modal_comment_ph")}
                value={approvalComment}
                onChange={e => setApprovalComment(e.target.value)}
                autoFocus
              />
            </div>
            <div className="wpd__modal-footer">
              <button className="wpd__btn wpd__btn--outline" onClick={() => setApprovalModal(null)} disabled={saving}>
                {t("cancel")}
              </button>
              <button
                className="wpd__btn wpd__btn--confirm"
                style={ACCION_COLOR[approvalModal.accion] ? {
                  background: ACCION_COLOR[approvalModal.accion].color,
                  borderColor: ACCION_COLOR[approvalModal.accion].color,
                  color: "#fff",
                } : {}}
                onClick={handleApproveLevel}
                disabled={saving}
              >
                {saving ? t("wpd_processing") : t("wpd_confirm")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal de cambio de estado operativo ── */}
      {opModal && (
        <div className="wpd__modal-overlay" onClick={() => setOpModal(null)}>
          <div className="wpd__modal" onClick={e => e.stopPropagation()}>
            <div className="wpd__modal-header">
              <span className="wpd__modal-title">{opModal.label}</span>
              <button className="wpd__modal-close" onClick={() => setOpModal(null)}>×</button>
            </div>
            <div className="wpd__modal-body">
              <p className="wpd__modal-desc">
                {t("wpd_confirm_q_pre")} <strong>{opModal.label}</strong>?
              </p>
              {opModal.requiresComment && (
                <>
                  <label className="wpd__modal-label">
                    {t("wpd_modal_reason_label")} <span className="wpd__modal-req">*</span>
                  </label>
                  <textarea
                    className="wpd__modal-textarea"
                    rows={3}
                    placeholder={t("wpd_modal_reason_ph")}
                    value={opComment}
                    onChange={e => setOpComment(e.target.value)}
                    autoFocus
                  />
                </>
              )}
              {!opModal.requiresComment && (
                <textarea
                  className="wpd__modal-textarea"
                  rows={2}
                  placeholder={t("wpd_modal_optional_ph")}
                  value={opComment}
                  onChange={e => setOpComment(e.target.value)}
                  autoFocus
                />
              )}

              {["suspended", "verified"].includes(opModal.status) && (
                <div className="wpd__modal-accion">
                  <label className="wpd__modal-accion__toggle">
                    <input
                      type="checkbox"
                      checked={opAccion.crear}
                      onChange={e => setOpAccion(p => ({ ...p, crear: e.target.checked }))}
                    />
                    {t("wpd_generate_op_action")}
                  </label>
                  {opAccion.crear && (
                    <div className="wpd__modal-accion__fields">
                      <div>
                        <label className="wpd__modal-label">{t("wpd_responsible")}</label>
                        <input
                          className="wpd__modal-input"
                          type="text"
                          placeholder={t("wpd_responsible_ph")}
                          value={opAccion.responsable}
                          onChange={e => setOpAccion(p => ({ ...p, responsable: e.target.value }))}
                        />
                      </div>
                      <div>
                        <label className="wpd__modal-label">{t("wpd_due_date")}</label>
                        <input
                          className="wpd__modal-input"
                          type="date"
                          value={opAccion.dueDate}
                          onChange={e => setOpAccion(p => ({ ...p, dueDate: e.target.value }))}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="wpd__modal-footer">
              <button className="wpd__btn wpd__btn--outline" onClick={() => setOpModal(null)} disabled={saving}>
                {t("cancel")}
              </button>
              <button
                className={`wpd__btn ${opModal.status === "suspended" ? "wpd__btn--stop" : "wpd__btn--start"}`}
                onClick={handleChangeStatus}
                disabled={saving}
              >
                {saving ? t("wpd_processing") : t("wpd_confirm")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal LMRA in-situ ── */}
      {lmraModal && (
        <div className="wpd__modal-overlay" onClick={() => setLmraModal(false)}>
          <div className="wpd__modal wpd__modal--lmra" onClick={e => e.stopPropagation()}>
            <div className="wpd__modal-header">
              <span className="wpd__modal-title"><Icon name="clipboard check" />{t("wpd_lmra_title")}</span>
              <button className="wpd__modal-close" onClick={() => setLmraModal(false)}>×</button>
            </div>
            <div className="wpd__modal-body">
              <div className="wpd__lmra-fecha">
                <label className="wpd__modal-label">{t("wpd_lmra_datetime")} <span className="wpd__modal-req">*</span></label>
                <input
                  type="datetime-local"
                  className="wpd__modal-input"
                  value={lmraForm.fecha}
                  onChange={e => setLmraForm(p => ({ ...p, fecha: e.target.value }))}
                />
              </div>
              <div className="wpd__lmra-preguntas">
                {lmraForm.preguntas.map((p, i) => (
                  <div key={i} className="wpd__lmra-item">
                    <p className="wpd__lmra-q"><span className="wpd__lmra-num">{i + 1}.</span> {t(`lmra_q${i + 1}`)}</p>
                    <div className="wpd__lmra-btns">
                      <button
                        className={`wpd__lmra-btn wpd__lmra-btn--si${p.respuesta === true ? " active" : ""}`}
                        onClick={() => setLmraPregunta(i, "respuesta", true)}
                      >{t("raf_yes")}</button>
                      <button
                        className={`wpd__lmra-btn wpd__lmra-btn--no${p.respuesta === false ? " active" : ""}`}
                        onClick={() => setLmraPregunta(i, "respuesta", false)}
                      >{t("raf_no")}</button>
                    </div>
                    <textarea
                      className="wpd__modal-textarea"
                      placeholder={p.respuesta === false ? t("wpd_lmra_condition_ph") : t("wpd_lmra_obs_ph")}
                      rows={2}
                      maxLength={300}
                      value={p.observacion}
                      onChange={e => setLmraPregunta(i, "observacion", e.target.value)}
                    />
                  </div>
                ))}
              </div>
              {lmraForm.preguntas.some(p => p.respuesta === false) && (
                <p className="wpd__lmra-warning">
                  <Icon name="warning sign" />
                  {t("wpd_lmra_warning_text")}
                </p>
              )}
            </div>
            <div className="wpd__modal-footer">
              <button className="wpd__btn wpd__btn--outline" onClick={() => setLmraModal(false)} disabled={savingLmra}>
                {t("cancel")}
              </button>
              <button className="wpd__btn wpd__btn--lmra" onClick={handleSaveLmra} disabled={savingLmra}>
                {savingLmra ? t("wpd_lmra_saving") : t("wpd_lmra_confirm")}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!confirmAction}
        title={t("wpd_confirm_title")}
        message={confirmAction?.message}
        onCancel={() => setConfirmAction(null)}
        onConfirm={() => { confirmAction?.run(); setConfirmAction(null); }}
        confirmDanger={!!confirmAction?.danger}
      />

    </div>
  );
}
